import React, { useState, useMemo } from "react";
import useAdminApi from "@/hooks/useAdminApi";
import SupplierLineagePanel from "./SupplierLineagePanel";

/**
 * CATALOG-2B (Feb 2026) — Admin import diff review (read-only).
 *
 * Read-only console at `/admin/catalog/import-review` for admin/staff
 * to review CATALOG-2/3 import jobs (dry-run + applied + rolled_back)
 * BEFORE any future apply or rollback action.
 *
 * Strict invariants honoured by this page:
 *   • Read-only. No POST / PATCH / DELETE.
 *   • NO apply / rollback / import buttons. A disabled hint surfaces
 *     the policy: "Apply requires separate explicit GO".
 *   • Endpoint already gates admin/staff; the FE additionally hides
 *     the page entry-point for everyone else through the route
 *     wrapping in `App.js`. The page itself, if reached anyway,
 *     would simply receive 403 and render the error state from
 *     `useAdminApi`.
 */
const AdminCatalogImportReview = () => {
  const [activeTab, setActiveTab] = useState("import-jobs");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterMode, setFilterMode] = useState("");
  const [filterSport, setFilterSport] = useState("");
  const [filterErrors, setFilterErrors] = useState("");
  const [selected, setSelected] = useState(null);

  const listParams = useMemo(() => {
    const p = { limit: 50 };
    if (filterStatus) p.status = filterStatus;
    if (filterMode) p.mode = filterMode;
    if (filterSport) p.sport = filterSport;
    if (filterErrors === "with") p.has_errors = true;
    if (filterErrors === "without") p.has_errors = false;
    return p;
  }, [filterStatus, filterMode, filterSport, filterErrors]);

  const { data, loading, error } = useAdminApi(
    activeTab === "import-jobs" ? "/admin/catalog/import-jobs" : null,
    { params: listParams },
  );
  const { data: detailData, loading: detailLoading } = useAdminApi(
    selected ? `/admin/catalog/import-jobs/${selected}` : null,
  );

  const items = data?.items || [];

  return (
    <section
      data-testid="admin-catalog-import-review"
      className="space-y-4 p-6"
    >
      <header>
        <h1 className="text-xl font-semibold text-slate-900">
          Catalog Import Review
        </h1>
        <p className="mt-1 text-xs text-slate-500">
          Read-only review of CATALOG-2 / CATALOG-3 import jobs. Apply
          and rollback require a separate explicit GO.
        </p>
      </header>

      {/* CATALOG-2B-SUPPLIER-LINEAGE tab switcher */}
      <nav
        data-testid="catalog-review-tabs"
        className="flex gap-1 border-b border-slate-200"
      >
        <TabButton
          testId="catalog-review-tab-import-jobs"
          active={activeTab === "import-jobs"}
          onClick={() => setActiveTab("import-jobs")}
          label="Import jobs"
        />
        <TabButton
          testId="catalog-review-tab-supplier-runs"
          active={activeTab === "supplier-runs"}
          onClick={() => setActiveTab("supplier-runs")}
          label="Supplier runs"
        />
      </nav>

      {activeTab === "supplier-runs" ? <SupplierLineagePanel /> : null}

      {activeTab === "import-jobs" ? (
        <ImportJobsPanel
          filterStatus={filterStatus}
          setFilterStatus={setFilterStatus}
          filterMode={filterMode}
          setFilterMode={setFilterMode}
          filterSport={filterSport}
          setFilterSport={setFilterSport}
          filterErrors={filterErrors}
          setFilterErrors={setFilterErrors}
          loading={loading}
          error={error}
          data={data}
          items={items}
          selected={selected}
          setSelected={setSelected}
          detailData={detailData}
          detailLoading={detailLoading}
        />
      ) : null}
    </section>
  );
};

const TabButton = ({ testId, active, onClick, label }) => (
  <button
    type="button"
    data-testid={testId}
    onClick={onClick}
    className={`px-3 py-1.5 text-xs font-medium ${
      active
        ? "border-b-2 border-emerald-600 text-emerald-700"
        : "text-slate-500 hover:text-slate-700"
    }`}
  >
    {label}
  </button>
);

const ImportJobsPanel = ({
  filterStatus,
  setFilterStatus,
  filterMode,
  setFilterMode,
  filterSport,
  setFilterSport,
  filterErrors,
  setFilterErrors,
  loading,
  error,
  data,
  items,
  selected,
  setSelected,
  detailData,
  detailLoading,
}) => {
  return (
    <>

      {/* Filters */}
      <div className="flex flex-wrap items-end gap-2 rounded-md border border-slate-200 bg-slate-50 p-3 text-xs">
        <Filter
          testId="catalog-import-filter-status"
          label="Status"
          value={filterStatus}
          onChange={setFilterStatus}
          options={[
            ["", "All"],
            ["dry_run", "dry_run"],
            ["applied", "applied"],
            ["rolled_back", "rolled_back"],
            ["failed", "failed"],
          ]}
        />
        <Filter
          testId="catalog-import-filter-mode"
          label="Mode"
          value={filterMode}
          onChange={setFilterMode}
          options={[
            ["", "All"],
            ["dry_run", "dry_run"],
            ["patch_dry_run", "patch_dry_run"],
            ["apply", "apply"],
            ["patch_apply", "patch_apply"],
          ]}
        />
        <Filter
          testId="catalog-import-filter-sport"
          label="Sport"
          value={filterSport}
          onChange={setFilterSport}
          options={[
            ["", "All"],
            ["tennis", "tennis"],
            ["padel", "padel"],
            ["badminton", "badminton"],
          ]}
        />
        <Filter
          testId="catalog-import-filter-errors"
          label="Errors"
          value={filterErrors}
          onChange={setFilterErrors}
          options={[
            ["", "All"],
            ["with", "with errors"],
            ["without", "no errors"],
          ]}
        />
        <span className="ml-auto text-[11px] text-slate-500">
          {loading
            ? "Loading…"
            : `${data?.total ?? 0} job(s) match the current filters`}
        </span>
      </div>

      {error ? (
        <p
          data-testid="catalog-import-error"
          className="rounded-md border border-rose-200 bg-rose-50 p-3 text-xs text-rose-800"
        >
          {String(error?.detail || error?.message || error)}
        </p>
      ) : null}

      {/* Jobs table */}
      <div className="overflow-hidden rounded-md border border-slate-200">
        <table
          data-testid="catalog-import-jobs-table"
          className="w-full text-left text-xs"
        >
          <thead className="bg-slate-100 text-[11px] uppercase tracking-wide text-slate-600">
            <tr>
              <th className="px-2 py-1.5">Job</th>
              <th className="px-2 py-1.5">Sport</th>
              <th className="px-2 py-1.5">Status</th>
              <th className="px-2 py-1.5">Mode</th>
              <th className="px-2 py-1.5">NEW</th>
              <th className="px-2 py-1.5">UPDATE</th>
              <th className="px-2 py-1.5">SKIP</th>
              <th className="px-2 py-1.5">ERROR</th>
              <th className="px-2 py-1.5">Warnings</th>
              <th className="px-2 py-1.5">Submitted</th>
              <th className="px-2 py-1.5">By</th>
              <th className="px-2 py-1.5">&nbsp;</th>
            </tr>
          </thead>
          <tbody className="text-slate-700">
            {items.length === 0 && !loading ? (
              <tr>
                <td colSpan={12} className="px-2 py-4 text-center text-slate-400">
                  No jobs match the current filters.
                </td>
              </tr>
            ) : (
              items.map((j) => (
                <tr
                  key={j.job_id}
                  data-testid={`catalog-import-job-row-${j.job_id}`}
                  className="border-t border-slate-100 hover:bg-slate-50"
                >
                  <td className="px-2 py-1.5 font-mono text-[10px] text-slate-500">
                    {j.job_id.slice(0, 8)}…
                  </td>
                  <td className="px-2 py-1.5">{j.sport || "—"}</td>
                  <td className="px-2 py-1.5">
                    <StatusBadge value={j.status} />
                  </td>
                  <td className="px-2 py-1.5">{j.mode || "—"}</td>
                  <td className="px-2 py-1.5 text-emerald-700">
                    {j.records_new ?? "—"}
                  </td>
                  <td className="px-2 py-1.5 text-sky-700">
                    {j.records_updated ?? "—"}
                  </td>
                  <td className="px-2 py-1.5 text-slate-500">
                    {j.records_skipped ?? "—"}
                  </td>
                  <td
                    className={`px-2 py-1.5 ${
                      (j.records_errors ?? 0) > 0
                        ? "text-rose-700 font-semibold"
                        : "text-slate-500"
                    }`}
                  >
                    {j.records_errors ?? "—"}
                  </td>
                  <td className="px-2 py-1.5">
                    {(j.validation_report?.warnings?.length ?? 0)}
                  </td>
                  <td className="px-2 py-1.5 text-[10px] text-slate-500">
                    {j.submitted_at ? j.submitted_at.slice(0, 19) : "—"}
                  </td>
                  <td className="px-2 py-1.5 text-[10px] text-slate-500">
                    {j.submitted_by || "—"}
                  </td>
                  <td className="px-2 py-1.5">
                    <button
                      type="button"
                      data-testid={`catalog-import-job-view-${j.job_id}`}
                      onClick={() => setSelected(j.job_id)}
                      className="rounded bg-slate-800 px-2 py-0.5 text-[10px] text-white hover:bg-slate-900"
                    >
                      View
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Detail */}
      {selected ? (
        <section
          data-testid="catalog-import-job-detail"
          className="rounded-md border border-slate-200 bg-white p-3 text-xs"
        >
          <header className="flex items-center justify-between">
            <h2 className="text-sm font-semibold">Job detail</h2>
            <button
              type="button"
              data-testid="catalog-import-job-detail-close"
              onClick={() => setSelected(null)}
              className="text-[10px] text-slate-500 hover:text-slate-700"
            >
              Close
            </button>
          </header>
          {detailLoading ? (
            <p className="mt-2 text-slate-400">Loading detail…</p>
          ) : detailData ? (
            <>
              <JobDetail job={detailData} />
              <BrandSupplierContextPanel jobId={selected} />
            </>
          ) : null}
        </section>
      ) : null}

      {/* Apply hint */}
      <p
        data-testid="catalog-import-apply-disabled-hint"
        className="rounded-md border border-amber-200 bg-amber-50 p-2 text-[11px] text-amber-800"
      >
        Apply requires separate explicit GO. This screen is read-only.
      </p>
    </>
  );
};

const Filter = ({ testId, label, value, onChange, options }) => (
  <label className="flex flex-col gap-1">
    <span className="text-[10px] uppercase tracking-wide text-slate-500">
      {label}
    </span>
    <select
      data-testid={testId}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="rounded-md border border-slate-300 px-2 py-1 text-xs"
    >
      {options.map(([v, lbl]) => (
        <option key={v} value={v}>
          {lbl}
        </option>
      ))}
    </select>
  </label>
);

const StatusBadge = ({ value }) => {
  const tone =
    value === "applied"
      ? "bg-emerald-100 text-emerald-800"
      : value === "rolled_back"
        ? "bg-amber-100 text-amber-800"
        : value === "failed"
          ? "bg-rose-100 text-rose-800"
          : "bg-slate-100 text-slate-700";
  return (
    <span
      className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-medium ${tone}`}
    >
      {value || "—"}
    </span>
  );
};

const JobDetail = ({ job }) => {
  const diff = job.diff_snapshot || {};
  const vr = job.validation_report || {};
  return (
    <div className="mt-2 space-y-3">
      <dl className="grid grid-cols-2 gap-2 text-[10px] sm:grid-cols-4">
        <Meta k="job_id" v={job.job_id} mono />
        <Meta k="catalog_version" v={job.catalog_version} mono />
        <Meta k="sport" v={job.sport} />
        <Meta k="environment" v={job.environment} />
        <Meta k="submitted_by" v={job.submitted_by} />
        <Meta k="submitted_at" v={job.submitted_at} />
        <Meta k="applied_at" v={job.applied_at} />
        <Meta k="rollback_snapshot_id" v={job.rollback_snapshot_id} mono />
      </dl>

      {vr.errors && vr.errors.length > 0 ? (
        <details
          data-testid="catalog-import-detail-errors"
          className="rounded-md border border-rose-200 bg-rose-50 p-2"
        >
          <summary className="cursor-pointer text-[11px] font-medium text-rose-800">
            Errors ({vr.errors.length})
          </summary>
          <ul className="mt-1 list-disc pl-5 text-[10px] text-rose-800">
            {vr.errors.slice(0, 200).map((e, i) => (
              <li key={i}>{typeof e === "string" ? e : JSON.stringify(e)}</li>
            ))}
          </ul>
        </details>
      ) : null}

      {vr.warnings && vr.warnings.length > 0 ? (
        <details
          data-testid="catalog-import-detail-warnings"
          className="rounded-md border border-amber-200 bg-amber-50 p-2"
        >
          <summary className="cursor-pointer text-[11px] font-medium text-amber-800">
            Warnings ({vr.warnings.length})
          </summary>
          <ul className="mt-1 list-disc pl-5 text-[10px] text-amber-800">
            {vr.warnings.slice(0, 200).map((w, i) => (
              <li key={i}>{typeof w === "string" ? w : JSON.stringify(w)}</li>
            ))}
          </ul>
        </details>
      ) : null}

      {Object.keys(diff).map((collection) => (
        <DiffBlock
          key={collection}
          collection={collection}
          buckets={diff[collection]}
        />
      ))}
    </div>
  );
};

const Meta = ({ k, v, mono = false }) => (
  <div>
    <p className="text-[9px] uppercase tracking-wide text-slate-400">{k}</p>
    <p className={`text-[10px] text-slate-700 ${mono ? "font-mono" : ""}`}>
      {v == null || v === "" ? "—" : String(v).slice(0, 64)}
    </p>
  </div>
);

const DiffBlock = ({ collection, buckets }) => {
  if (!buckets || typeof buckets !== "object") return null;
  return (
    <details
      data-testid={`catalog-import-detail-diff-${collection}`}
      className="rounded-md border border-slate-200 bg-white p-2"
      open
    >
      <summary className="cursor-pointer text-[11px] font-medium text-slate-700">
        {collection}
      </summary>
      <div className="mt-2 grid grid-cols-3 gap-2">
        {["NEW", "UPDATE", "SKIP"].map((k) => (
          <DiffBucket
            key={k}
            label={k}
            collection={collection}
            entries={buckets[k] || []}
          />
        ))}
      </div>
    </details>
  );
};

const DiffBucket = ({ label, collection, entries }) => {
  const tone =
    label === "NEW"
      ? "border-emerald-200 bg-emerald-50 text-emerald-800"
      : label === "UPDATE"
        ? "border-sky-200 bg-sky-50 text-sky-800"
        : "border-slate-200 bg-slate-50 text-slate-700";
  return (
    <div
      data-testid={`catalog-import-detail-diff-${collection}-${label.toLowerCase()}`}
      className={`rounded-md border p-2 ${tone}`}
    >
      <p className="text-[10px] font-semibold uppercase">
        {label} ({entries.length})
      </p>
      <ul className="mt-1 max-h-48 list-disc overflow-auto pl-4 text-[10px]">
        {entries.slice(0, 200).map((e, i) => (
          <li key={i} className="font-mono">
            {typeof e === "string" ? e : JSON.stringify(e)}
          </li>
        ))}
        {entries.length > 200 ? (
          <li className="italic">…and {entries.length - 200} more</li>
        ) : null}
      </ul>
    </div>
  );
};

export default AdminCatalogImportReview;

// ---------------------------------------------------------------------------
// CATALOG-2B-BRAND-SUPPLIER-CONTEXT (Jun 2026) — inline read-only panel.
//
// Fetches `/admin/catalog/import-jobs/{job_id}/brand-supplier-context` and
// renders the brand→supplier context (linked entities + relationships)
// grouped by brand for every product referenced in the job's diff snapshot.
// Strict invariants:
//   • Read-only (no buttons, no writes, no marketplace activation).
//   • No OEM inference (Gamma surfaces w008_flag, NEVER an OEM row).
//   • Brands absent from the job (e.g. Prince/Völkl in v1.1) are not invented.
// ---------------------------------------------------------------------------

const BrandSupplierContextPanel = ({ jobId }) => {
  const { data, loading, error } = useAdminApi(
    jobId ? `/admin/catalog/import-jobs/${jobId}/brand-supplier-context` : null,
  );

  if (!jobId) return null;

  return (
    <section
      data-testid="catalog-import-brand-supplier-context"
      className="mt-4 rounded-md border border-emerald-200 bg-emerald-50/40 p-3"
    >
      <header className="flex items-center justify-between">
        <h3 className="text-xs font-semibold text-emerald-900">
          Brand → Supplier context (read-only)
        </h3>
        {data?.totals ? (
          <span
            data-testid="catalog-import-brand-supplier-context-totals"
            className="text-[10px] text-emerald-800"
          >
            {data.totals.products} product(s) · {data.totals.brands} brand(s) ·{" "}
            {data.totals.brands_with_context} with context ·{" "}
            {data.totals.brands_without_context} pending
          </span>
        ) : null}
      </header>

      {loading ? (
        <p className="mt-2 text-[11px] text-emerald-700">Loading supplier context…</p>
      ) : null}

      {error ? (
        <p
          data-testid="catalog-import-brand-supplier-context-error"
          className="mt-2 rounded border border-rose-200 bg-rose-50 p-2 text-[11px] text-rose-800"
        >
          {String(error?.detail || error?.message || error)}
        </p>
      ) : null}

      {data && !loading ? (
        <>
          {data.brands.length === 0 ? (
            <p
              data-testid="catalog-import-brand-supplier-context-empty"
              className="mt-2 text-[11px] italic text-emerald-700"
            >
              No branded products referenced in this job.
            </p>
          ) : (
            <ul className="mt-2 space-y-2">
              {data.brands.map((b) => (
                <BrandContextCard key={b.brand_internal_id} brand={b} />
              ))}
            </ul>
          )}

          {data.policy ? (
            <p className="mt-2 text-[10px] italic text-emerald-700/80">
              Policy: {data.policy}
            </p>
          ) : null}
        </>
      ) : null}
    </section>
  );
};

const BrandContextCard = ({ brand }) => {
  const {
    brand_internal_id,
    brand_name,
    products_in_job = [],
    linked_entities = [],
    relationships = [],
    has_supplier_context,
    empty_message,
    w008_flag,
  } = brand;

  return (
    <li
      data-testid={`catalog-import-brand-context-${brand_internal_id}`}
      className="rounded-md border border-emerald-200 bg-white p-2"
    >
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-mono text-[10px] text-slate-500">
          {brand_internal_id}
        </span>
        <span className="text-[11px] font-semibold text-slate-800">
          {brand_name || "—"}
        </span>
        <span className="text-[10px] text-slate-500">
          ({products_in_job.length} product
          {products_in_job.length === 1 ? "" : "s"})
        </span>
        {has_supplier_context ? (
          <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-medium text-emerald-800">
            context linked
          </span>
        ) : (
          <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-600">
            no supplier context yet
          </span>
        )}
        {w008_flag ? (
          <span
            data-testid={`catalog-import-brand-context-${brand_internal_id}-w008`}
            className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-medium text-amber-800"
            title="W008 — OEM relationship not inferred. Awaiting human resolution."
          >
            W008 · OEM not inferred
          </span>
        ) : null}
      </div>

      {products_in_job.length > 0 ? (
        <p className="mt-1 font-mono text-[10px] text-slate-500">
          {products_in_job.slice(0, 8).join(", ")}
          {products_in_job.length > 8
            ? `, …+${products_in_job.length - 8} more`
            : ""}
        </p>
      ) : null}

      {linked_entities.length > 0 ? (
        <div className="mt-2">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">
            Linked supplier entities ({linked_entities.length})
          </p>
          <ul className="mt-1 grid grid-cols-1 gap-1 sm:grid-cols-2">
            {linked_entities.map((e) => (
              <li
                key={e.internal_id}
                data-testid={`catalog-import-brand-context-${brand_internal_id}-entity-${e.internal_id}`}
                className="rounded border border-slate-200 bg-slate-50 px-2 py-1 text-[10px]"
              >
                <span className="font-mono text-slate-500">{e.internal_id}</span>{" "}
                <span className="text-slate-800">{e.name || "—"}</span>{" "}
                <span className="text-slate-500">
                  · {e.entity_type || "—"}
                  {e.country ? ` · ${e.country}` : ""} · {e.status || "—"}
                </span>
                {e.needs_human_review ? (
                  <span className="ml-1 rounded bg-amber-100 px-1 text-[9px] text-amber-800">
                    needs review
                  </span>
                ) : null}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {relationships.length > 0 ? (
        <div className="mt-2">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">
            Brand relationships ({relationships.length})
          </p>
          <ul className="mt-1 space-y-1">
            {relationships.map((r) => (
              <li
                key={r.internal_id}
                data-testid={`catalog-import-brand-context-${brand_internal_id}-rel-${r.internal_id}`}
                className="rounded border border-slate-200 bg-white px-2 py-1 text-[10px]"
              >
                <span className="font-mono text-slate-500">{r.internal_id}</span>{" "}
                <span className="font-medium text-slate-800">
                  {r.relationship_type || "—"}
                </span>{" "}
                <span className="text-slate-500">→</span>{" "}
                <span className="font-mono text-slate-500">
                  {r.supplier_internal_id || "—"}
                </span>{" "}
                <span className="text-slate-800">
                  {r.supplier_name ? `(${r.supplier_name})` : ""}
                </span>{" "}
                <span className="text-slate-500">
                  · {r.territory || "—"} · {r.status || "—"}
                </span>
                {typeof r.confidence === "number" ? (
                  <span className="ml-1 text-slate-500">
                    · conf {r.confidence.toFixed(2)}
                  </span>
                ) : null}
                {r.needs_human_review ? (
                  <span className="ml-1 rounded bg-amber-100 px-1 text-[9px] text-amber-800">
                    needs review
                  </span>
                ) : null}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {!has_supplier_context && empty_message ? (
        <p
          data-testid={`catalog-import-brand-context-${brand_internal_id}-empty`}
          className="mt-2 text-[10px] italic text-slate-500"
        >
          {empty_message}
        </p>
      ) : null}
    </li>
  );
};
